// Espelho de backend/utils/orcamentoPagamento.js para o preview ao vivo no admin.
// O backend recalcula e é a fonte de verdade.

export const MIN_PARCELAS = 2;
export const MAX_PARCELAS = 24;

export interface OpcoesPagamento {
  avista: { habilitado: boolean };
  avistaDesconto: { habilitado: boolean; percentual: number };
  parcelados: { parcelas: number; juros: number }[];
}

export type TipoOpcao = 'avista' | 'avista_desconto' | 'parcelado';

export interface OpcaoCalculada {
  id: string;
  tipo: TipoOpcao;
  parcelas: number;
  percentual: number;
  valorBase: number;
  valorFinal: number;
  valorParcela: number;
  valorUltimaParcela?: number;
}

export const emptyOpcoesPagamento = (): OpcoesPagamento => ({
  avista: { habilitado: true },
  avistaDesconto: { habilitado: false, percentual: 0 },
  parcelados: []
});

const round2 = (n: number) => Math.round(n * 100) / 100;

const clampPercent = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(round2(n), 100);
};

export const temOpcoes = (o?: Partial<OpcoesPagamento> | null) =>
  !!o && (!!o.avista?.habilitado || !!o.avistaDesconto?.habilitado || (o.parcelados?.length ?? 0) > 0);

export function calcularOpcoes(valorBase: number, o?: Partial<OpcoesPagamento> | null): OpcaoCalculada[] {
  if (!o || !temOpcoes(o)) return [];
  const base = round2(Number(valorBase) || 0);
  const opcoes: OpcaoCalculada[] = [];

  if (o.avista?.habilitado) {
    opcoes.push({ id: 'avista', tipo: 'avista', parcelas: 1, percentual: 0, valorBase: base, valorFinal: base, valorParcela: base });
  }
  if (o.avistaDesconto?.habilitado) {
    const pct = clampPercent(o.avistaDesconto.percentual);
    const final = round2(base * (1 - pct / 100));
    opcoes.push({ id: 'avista_desconto', tipo: 'avista_desconto', parcelas: 1, percentual: pct, valorBase: base, valorFinal: final, valorParcela: final });
  }

  const vistos = new Set<number>();
  [...(o.parcelados || [])]
    .filter((p) => Number.isInteger(p.parcelas) && p.parcelas >= MIN_PARCELAS && p.parcelas <= MAX_PARCELAS)
    .sort((a, b) => a.parcelas - b.parcelas)
    .forEach((p) => {
      if (vistos.has(p.parcelas)) return;
      vistos.add(p.parcelas);
      const n = p.parcelas;
      const pct = clampPercent(p.juros);
      const final = round2(base * (1 + pct / 100));
      const valorParcela = Math.floor((final / n) * 100) / 100;
      opcoes.push({
        id: `parcelado_${n}`,
        tipo: 'parcelado',
        parcelas: n,
        percentual: pct,
        valorBase: base,
        valorFinal: final,
        valorParcela,
        valorUltimaParcela: round2(final - valorParcela * (n - 1))
      });
    });

  return opcoes;
}

export interface ItemTotalizavel {
  valor: number | string;
  quantidade?: number | string;
  abatidoAoFecharOutros?: boolean;
}

export interface AbatimentoManual {
  valor: number;
  descricao: string;
}

export interface Totais {
  subtotal: number;
  abatimentoItens: number;
  abatimentoManual: number;
  abatimento: number;
  total: number;
}

// Espelho de calcularTotais do backend: itens abatidos saem do total se houver outro item cobrado.
export function calcularTotais(itens: ItemTotalizavel[], abatimentoManual?: Partial<AbatimentoManual> | null): Totais {
  const valorItem = (item: ItemTotalizavel) => (Number(item.valor) || 0) * (Number(item.quantidade) || 1);
  const subtotal = round2(itens.reduce((acc, item) => acc + valorItem(item), 0));
  const temOutroServico = itens.some((item) => !item.abatidoAoFecharOutros && valorItem(item) > 0);
  const abatimentoItens = temOutroServico
    ? round2(itens.filter((item) => item.abatidoAoFecharOutros).reduce((acc, item) => acc + valorItem(item), 0))
    : 0;
  const manual = Math.max(round2(Number(abatimentoManual?.valor) || 0), 0);
  const abatimento = round2(Math.min(abatimentoItens + manual, subtotal));
  return { subtotal, abatimentoItens, abatimentoManual: manual, abatimento, total: round2(subtotal - abatimento) };
}

export const formatEuro = (n: number) => `€${Number(n || 0).toFixed(2)}`;

// Rótulo curto em pt para o admin (a página pública usa i18n).
export function labelOpcaoAdmin(o?: Pick<OpcaoCalculada, 'tipo' | 'parcelas' | 'percentual' | 'valorParcela'> | null) {
  if (!o) return '';
  if (o.tipo === 'avista') return 'À vista';
  if (o.tipo === 'avista_desconto') return `À vista com ${o.percentual}% de desconto`;
  return `${o.parcelas}x de ${formatEuro(o.valorParcela)} (${o.percentual > 0 ? `${o.percentual}% de juros` : 'sem juros'})`;
}
